<template>
  <div
    class="app-skeleton"
    :class="{
      'app-skeleton--inline': inline,
      'app-skeleton--rows': pattern === 'rows',
    }"
  >
    <div
      v-for="(bar, idx) in bars"
      :key="idx"
      class="app-skeleton__bar"
      :class="`app-skeleton__bar--${bar.width}`"
      :style="barStyle(bar)"
    />
  </div>
</template>

<script>
/**
 * Shimmer-skeleton loader. Renders N bars with varying widths to suggest
 * content shape while real data loads.
 *
 * Usage:
 *   <app-skeleton :lines="4" />                      // 4 bars, default mix
 *   <app-skeleton :lines="3" pattern="hero" />       // first bar 30%, rest mixed
 *   <app-skeleton :lines="5" pattern="rows" />       // table rows: even, full-width
 *   <app-skeleton :pattern="[100, 65, 100, 35]" />   // explicit % widths
 */
export default {
  name: 'AppSkeleton',

  props: {
    // Number of bars to render (used when `pattern` is the default name).
    lines: {
      type: Number,
      default: 4,
    },
    // Named preset OR array of explicit widths in %.
    pattern: {
      type: [String, Array],
      default: 'default',
    },
    // Switch from block (column stack) to inline-flex compact mode.
    inline: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    bars() {
      // Array pattern — explicit widths, applied through an inline style.
      if (Array.isArray(this.pattern)) {
        return this.pattern.map((w) => ({ width: 'custom', customWidth: `${w}%` }))
      }

      const n = Math.max(1, this.lines)
      const out = []

      if (this.pattern === 'hero') {
        // First bar reads as a title, the rest as body copy.
        out.push({ width: 'lg' })
        for (let i = 1; i < n; i++) {
          out.push({ width: i % 2 === 1 ? 'full' : 'md' })
        }
      } else if (this.pattern === 'rows') {
        // Table placeholder — even, full-width bars so the shimmer reads as
        // a list of rows rather than a paragraph.
        for (let i = 0; i < n; i++) {
          out.push({ width: 'full', height: '18px' })
        }
      } else {
        // "default" pattern — alternating full + md
        for (let i = 0; i < n; i++) {
          out.push({ width: i % 2 === 0 ? 'full' : 'md' })
        }
      }

      return out
    },
  },

  methods: {
    barStyle(bar) {
      const style = {}
      if (bar.customWidth) style.width = bar.customWidth
      if (bar.height) style.height = bar.height

      return Object.keys(style).length ? style : null
    },
  },
}
</script>

<style lang="scss" scoped>
$as-bg-soft:  #f6f8fa;
$as-divider:  rgba(0, 0, 0, 0.06);

.app-skeleton {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 4px 0;

  // Row placeholders sit further apart so the shimmer reads as table rows.
  &--rows { gap: 16px; }

  &--inline {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    padding: 0;
  }

  &__bar {
    height: 12px;
    border-radius: 4px;
    background: linear-gradient(
      90deg,
      $as-bg-soft 0%,
      $as-divider 50%,
      $as-bg-soft 100%
    );
    background-size: 200% 100%;
    animation: app-skeleton-shimmer 1.2s ease-in-out infinite;

    // ── Width variants ──
    &--full { width: 100%; }
    &--md   { width: 65%;  }
    &--lg   { height: 16px; width: 30%; }
    &--sm   { width: 35%;  }
  }
}

@keyframes app-skeleton-shimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
</style>
